// SPDX-License-Identifier: AGPL-3.0-or-later

import {Button} from '@app/features/ui/button/Button';
import {Slider} from '@app/features/ui/components/Slider';
import {setRoomVideo, stopMemberRoomVideo, stopRoomVideo} from '@app/features/voice_scene/commands/VoiceSeatCommands';
import styles from '@app/features/voice_scene/components/VoiceSceneTv.module.css';
import {TvIconButton} from '@app/features/voice_scene/components/VoiceSceneTvIconButton';
import {serverNow} from '@app/features/voice_scene/net/RoomServerClock';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {type VoiceWatch, voiceWatchPositionMs} from '@fluxer/constants/src/VoiceRoomWatch';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	ClosedCaptioningIcon,
	FastForwardIcon,
	PauseIcon,
	PlayIcon,
	RewindIcon,
	SpeakerHighIcon,
	SpeakerXIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

const PLAY_DESCRIPTOR = msg({
	message: 'Play',
	comment:
		'3D voice room TV: button of the person who put a video on the TV; plays it again for everyone in the room from where it was paused.',
});
const PAUSE_DESCRIPTOR = msg({
	message: 'Pause',
	comment: '3D voice room TV: button of the person who put a video on the TV; pauses it for everyone in the room.',
});
const BACK_DESCRIPTOR = msg({
	message: 'Back 10 seconds',
	comment: '3D voice room TV: button of the person who put a video on the TV; jumps it back 10 seconds for everyone.',
});
const FORWARD_DESCRIPTOR = msg({
	message: 'Forward 10 seconds',
	comment: '3D voice room TV: button of the person who put a video on the TV; jumps it ahead 10 seconds for everyone.',
});
const STOP_DESCRIPTOR = msg({
	message: 'Stop video',
	comment: '3D voice room TV: button of the person who put a video on the TV; takes it off the TV for everyone.',
});
const MODERATOR_STOP_DESCRIPTOR = msg({
	message: 'Stop video (moderator)',
	comment:
		"3D voice room TV: button shown to a moderator (mute or move members permission) on someone else's video; takes it off the TV for everyone in the room.",
});
const VIDEO_VOLUME_DESCRIPTOR = msg({
	message: 'Video volume',
	comment:
		"3D voice room TV: accessible name of the slider for how loud the TV's video plays for this viewer only. Remembered on this device.",
});
const VIDEO_VOLUME_VALUE_DESCRIPTOR = msg({
	message: '{percent}%',
	comment: 'Voice call 3D room TV: spoken value of the video volume slider. {percent} is a number from 0 to 100.',
});

const CAPTIONS_ON_DESCRIPTOR = msg({
	message: 'Turn on subtitles',
	comment:
		"3D voice room TV: button that shows the video's YouTube subtitles, for this viewer only (off by default). Remembered on this device.",
});
const CAPTIONS_OFF_DESCRIPTOR = msg({
	message: 'Turn off subtitles',
	comment: "3D voice room TV: button that hides the video's YouTube subtitles again, for this viewer only.",
});

const SKIP_MS = 10_000;

// The opener's buttons: each sends the whole new state (from the shared position now) to the server, which writes it
// on the opener's attribute; every TV, the opener's too, follows that attribute.
export const VoiceSceneTvVideoControls = observer(function VoiceSceneTvVideoControls({
	channelId,
	watch,
}: {
	channelId: string;
	watch: VoiceWatch;
}) {
	const {i18n} = useLingui();
	const paused = watch.pausedAtMs !== null;
	const send = (skipMs: number, nextPaused: boolean): void => {
		void setRoomVideo(channelId, {
			videoId: watch.videoId,
			positionMs: voiceWatchPositionMs(watch, serverNow()) + skipMs,
			paused: nextPaused,
		});
	};
	return (
		<>
			<TvIconButton
				label={paused ? PLAY_DESCRIPTOR : PAUSE_DESCRIPTOR}
				icon={
					paused ? (
						<PlayIcon weight="fill" size={16} data-flx="voice-scene.voice-scene-tv-video-controls.play-icon" />
					) : (
						<PauseIcon weight="fill" size={16} data-flx="voice-scene.voice-scene-tv-video-controls.pause-icon" />
					)
				}
				onClick={() => send(0, !paused)}
				data-flx="voice-scene.voice-scene-tv-video-controls.tv-icon-button.send"
			/>
			<TvIconButton
				label={BACK_DESCRIPTOR}
				icon={<RewindIcon weight="fill" size={16} data-flx="voice-scene.voice-scene-tv-video-controls.rewind-icon" />}
				onClick={() => send(-SKIP_MS, paused)}
				data-flx="voice-scene.voice-scene-tv-video-controls.tv-icon-button.send--2"
			/>
			<TvIconButton
				label={FORWARD_DESCRIPTOR}
				icon={
					<FastForwardIcon
						weight="fill"
						size={16}
						data-flx="voice-scene.voice-scene-tv-video-controls.fast-forward-icon"
					/>
				}
				onClick={() => send(SKIP_MS, paused)}
				data-flx="voice-scene.voice-scene-tv-video-controls.tv-icon-button.send--3"
			/>
			<Button
				small
				compact
				variant="inverted"
				onClick={() => void stopRoomVideo(channelId)}
				data-flx="voice-scene.voice-scene-tv-video-controls.button"
			>
				{i18n._(STOP_DESCRIPTOR)}
			</Button>
		</>
	);
});

// A moderator's button on someone else's video: the server checks MUTE_MEMBERS or MOVE_MEMBERS again.
export function VoiceSceneTvModeratorStop({channelId, userId}: {channelId: string; userId: string}) {
	const {i18n} = useLingui();
	return (
		<Button
			small
			compact
			variant="inverted"
			onClick={() => void stopMemberRoomVideo(channelId, userId)}
			data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-moderator-stop.button"
		>
			{i18n._(MODERATOR_STOP_DESCRIPTOR)}
		</Button>
	);
}

// The video's subtitles for this viewer: off until turned on.
export const VoiceSceneTvCaptions = observer(function VoiceSceneTvCaptions() {
	const on = VoiceSceneSettings.tvCaptions;
	return (
		<TvIconButton
			label={on ? CAPTIONS_OFF_DESCRIPTOR : CAPTIONS_ON_DESCRIPTOR}
			icon={
				<ClosedCaptioningIcon
					weight={on ? 'fill' : 'regular'}
					size={16}
					data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-captions.icon"
				/>
			}
			onClick={() => VoiceSceneSettings.setTvCaptions(!on)}
			data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-captions.tv-icon-button.set-tv-captions"
		/>
	);
});

// How loud the TV's video plays for this viewer, 0 (silent) to 100.
export const VoiceSceneTvVolume = observer(function VoiceSceneTvVolume() {
	const {i18n} = useLingui();
	const label = i18n._(VIDEO_VOLUME_DESCRIPTOR);
	const percent = Math.round(VoiceSceneSettings.tvVideoVolume * 100);
	const Icon = percent === 0 ? SpeakerXIcon : SpeakerHighIcon;
	return (
		<div className={styles.volume} data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-volume.volume">
			<Icon
				className={styles.volumeIcon}
				aria-hidden="true"
				data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-volume.volume-icon"
			/>
			<div
				className={styles.volumeSlider}
				data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-volume.volume-slider"
			>
				<Slider
					mini
					value={percent}
					defaultValue={percent}
					factoryDefaultValue={50}
					minValue={0}
					maxValue={100}
					step={1}
					ariaLabel={label}
					ariaValueText={i18n._(VIDEO_VOLUME_VALUE_DESCRIPTOR, {percent})}
					onValueChange={(value) => VoiceSceneSettings.setTvVideoVolume(Math.round(value) / 100)}
					data-flx="voice-scene.voice-scene-tv-video-controls.voice-scene-tv-volume.slider"
				/>
			</div>
		</div>
	);
});
